import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../firebase';
import DOMPurify from 'dompurify';
import { Helmet } from 'react-helmet-async';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Calendar, User, ArrowRight, Heart, Copy, Check, Loader2 } from 'lucide-react';
import { FacebookShareButton, TwitterShareButton, WhatsappShareButton, FacebookIcon, TwitterIcon, WhatsappIcon } from 'react-share';

export default function ArticleDetailPage({ theme, toggleTheme }) {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liking, setLiking] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  useEffect(() => {
    // Scroll to top
    window.scrollTo(0, 0);

    const fetchArticle = async () => {
      try {
        const docRef = doc(db, 'articles', id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          let dateStr = 'تاريخ غير معروف';
          if (data.createdAt) {
            const d = data.createdAt.toDate ? data.createdAt.toDate() : new Date(data.createdAt);
            dateStr = d.toLocaleDateString('ar-EG', { year: 'numeric', month: 'long', day: 'numeric' });
          }
          setArticle({ id: docSnap.id, ...data, dateStr });
        } else {
          setError('المقالة غير موجودة أو تم حذفها.');
        }
      } catch (err) {
        console.error(err);
        setError('حدث خطأ أثناء جلب المقالة.');
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [id]);

  const handleLike = async () => {
    if (liking || !article) return;
    setLiking(true);
    try {
      const docRef = doc(db, 'articles', id);
      await updateDoc(docRef, { likesCount: increment(1) });
      setArticle(prev => ({ ...prev, likesCount: (prev.likesCount || 0) + 1 }));
    } catch (err) {
      console.error(err);
    } finally {
      setLiking(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(article?.shareUrl || shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen bg-slate-50 dark:bg-zinc-950 transition-colors duration-500" dir="rtl">
        <Navbar theme={theme} toggleTheme={toggleTheme} />
        <div className="flex-grow flex flex-col items-center justify-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary-600 mb-4" />
          <p className="font-bold text-lg text-slate-500">جاري تحميل المقالة...</p>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="flex flex-col min-h-screen bg-slate-50 dark:bg-zinc-950 transition-colors duration-500" dir="rtl">
        <Navbar theme={theme} toggleTheme={toggleTheme} />
        <div className="flex-grow flex flex-col items-center justify-center p-4">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-4">{error}</h1>
          <Link to="/blog" className="text-primary-600 hover:underline flex items-center gap-2">
            <ArrowRight className="w-4 h-4" /> <span>العودة للمدونة</span>
          </Link>
        </div>
      </div>
    );
  }

  const plainTextDescription = article.content
    ? article.content.replace(/<[^>]+>/g, '').substring(0, 150) + '...'
    : 'اقرأ هذه المقالة الحصرية على منصة لباب.';

  return (
    <div className="flex flex-col min-h-screen bg-slate-50 dark:bg-zinc-950 font-sans transition-colors duration-500 overflow-x-hidden" dir="rtl">
      <Helmet>
        <title>{`${article.title} | منصة لباب`}</title>
        <meta name="description" content={plainTextDescription} />

        {/* Open Graph / Facebook / WhatsApp */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={shareUrl} />
        <meta property="og:title" content={`${article.title} | لباب`} />
        <meta property="og:description" content={plainTextDescription} />
        <meta property="og:image" content={article.coverImageUrl || 'https://lobab-course.web.app/lobab_cover_fallback.png'} />

        {/* Twitter / X */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={shareUrl} />
        <meta name="twitter:title" content={`${article.title} | لباب`} />
        <meta name="twitter:description" content={plainTextDescription} />
        <meta name="twitter:image" content={article.coverImageUrl || 'https://lobab-course.web.app/lobab_cover_fallback.png'} />
      </Helmet>

      <Navbar theme={theme} toggleTheme={toggleTheme} />

      <main className="flex-grow pt-8 pb-20 relative">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl relative z-10 animate-in fade-in slide-in-from-bottom-8 duration-700">

          <Link to="/blog" className="inline-flex items-center gap-2 text-mdfont-bold text-slate-500 hover:text-primary-600 dark:text-zinc-400 dark:hover:text-primary-400 mb-8 transition-colors">
            <ArrowRight className="w-4 h-4" />
            <span>جميع المقالات</span>
          </Link>

          {/* Header */}
          <header className="mb-10 text-center md:text-right">
            <div className="inline-flex items-center gap-2 py-1.5 px-4 rounded-full bg-primary-50 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400 text-mdfont-bold mb-6">
              {article.category || 'عام'}
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 leading-tight text-slate-900 dark:text-white">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-6 text-mdfont-bold text-slate-500 dark:text-zinc-400">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-slate-200 dark:bg-zinc-800 flex items-center justify-center text-slate-600 dark:text-zinc-300">
                  <User className="w-5 h-5" />
                </div>
                <span>{article.authorName}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 opacity-70" />
                <span>{article.dateStr}</span>
              </div>
            </div>
          </header>

          {/* Cover Image */}
          {article.coverImageUrl && (
            <div className="w-full aspect-[16/9] md:aspect-[21/9] rounded-lg overflow-hidden mb-12 shadow-2xl">
              <img src={article.coverImageUrl} alt={article.title} className="w-full h-full object-cover" />
            </div>
          )}

          {/* Content */}
          <article className="prose prose-lg md:prose-xl prose-slate dark:prose-invert max-w-none w-full mb-16 leading-loose 
                              prose-headings:font-bold prose-headings:text-slate-900 dark:prose-headings:text-white 
                              prose-a:text-primary-600 prose-a:no-underline hover:prose-a:underline dark:prose-a:text-primary-500 
                              prose-img:rounded-3xl prose-img:shadow-2xl prose-img:w-full prose-img:object-cover prose-img:my-10 
                              prose-ul:list-disc prose-ol:list-decimal prose-li:marker:text-primary-600 
                              prose-blockquote:border-r-4 prose-blockquote:border-l-0 prose-blockquote:border-primary-500 prose-blockquote:bg-slate-50 dark:prose-blockquote:bg-zinc-900/50 prose-blockquote:rounded-l-2xl prose-blockquote:py-3 prose-blockquote:px-6 prose-blockquote:text-slate-700 dark:prose-blockquote:text-zinc-300 prose-blockquote:not-italic prose-blockquote:shadow-sm
                              prose-strong:text-slate-900 dark:prose-strong:text-white">
            <style>{`
              .prose, .prose * {
                word-wrap: normal !important;
                overflow-wrap: normal !important;
                word-break: normal !important;
                white-space: normal !important;
                hyphens: none !important;
                direction: rtl !important;
              }
              .prose p, .prose li {
                line-height: 2 !important;
                margin-bottom: 1.5rem !important;
                text-align: right !important;
                text-justify: none !important;
                padding-left: 8px !important;
                padding-right: 8px !important;
              }
              /* Support Quill Alignment classes safely */
              .prose .ql-align-center { text-align: center !important; }
              .prose .ql-align-left { text-align: left !important; }
              .prose .ql-align-right { text-align: right !important; }
              .prose .ql-align-justify { text-align: right !important; }
            `}</style>
            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize((article.content || '').replace(/&nbsp;/g, ' ')) }} />
          </article>

          {/* Engagement */}
          <div className="border-t border-slate-200 dark:border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-8">
            <button
              onClick={handleLike}
              disabled={liking}
              className="group flex items-center gap-3 px-8 py-4 bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-2xl shadow-sm hover:shadow-xl hover:border-red-200 dark:hover:border-red-900 transition-all active:scale-95 disabled:opacity-50"
            >
              <Heart className={`w-6 h-6 ${liking ? 'animate-ping' : ''} text-red-500 group-hover:fill-red-500 transition-all`} />
              <span className="font-extrabold text-lg text-slate-900 dark:text-white">
                أعجبني ({article.likesCount || 0})
              </span>
            </button>

            <div className="flex items-center gap-3">
              <span className="font-bold text-slate-500 dark:text-zinc-400 ml-2">مشاركة: </span>
              <FacebookShareButton url={article.shareUrl || shareUrl} quote={article.title}>
                <FacebookIcon size={40} round />
              </FacebookShareButton>
              <TwitterShareButton url={article.shareUrl || shareUrl} title={article.title}>
                <TwitterIcon size={40} round />
              </TwitterShareButton>
              <WhatsappShareButton url={article.shareUrl || shareUrl} title={article.title}>
                <WhatsappIcon size={40} round />
              </WhatsappShareButton>
              <button
                onClick={copyLink}
                className="w-10 h-10 flex items-center justify-center bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 rounded-full transition-colors"
                title="نسخ الرابط"
              >
                {copied ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5 text-slate-600 dark:text-zinc-400" />}
              </button>
            </div>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
}
